/**
 * CodeMirror 6 — 간이 LookML 구문 하이라이트
 *
 * StreamLanguage 기반으로 다음을 하이라이트합니다:
 *   - 키워드: view, explore, dimension, measure, join, filter, ...
 *   - 타입값: string, number, count, sum, average, date, ...
 *   - 문자열: "..." 리터럴
 *   - 주석: # 라인 주석
 *   - 특수: ;; (LookML 구문 종료), ${...} (필드 참조)
 *   - 중괄호: { }
 */
import { StreamLanguage } from '@codemirror/language'

const KEYWORDS = new Set([
  'view', 'explore', 'join', 'dimension', 'dimension_group',
  'measure', 'filter', 'parameter', 'set', 'access_grant',
  'sql_table_name', 'sql', 'sql_on', 'type', 'label',
  'group_label', 'description', 'tags', 'hidden', 'primary_key',
  'value_format', 'value_format_name', 'drill_fields',
  'relationship', 'from', 'required_joins', 'always_filter',
  'conditionally_filter', 'timeframes', 'intervals', 'convert_tz',
  'datatype', 'sql_where', 'html', 'link', 'action',
  'derived_table', 'extends', 'include', 'connection', 'model',
  'persist_for', 'persist_with', 'datagroup_trigger',
  'max_cache_age', 'sql_trigger_value', 'group_item_label',
  'allow_approximate_optimization', 'fanout_on',
  'required_access_grants', 'can', 'suggestions',
  'suggest_dimension', 'suggest_explore',
])

const TYPE_VALUES = new Set([
  'string', 'number', 'yesno', 'date', 'date_time',
  'date_raw', 'date_date', 'date_week', 'date_month',
  'date_quarter', 'date_year', 'date_hour', 'date_minute',
  'date_second', 'time', 'timestamp',
  'count', 'count_distinct', 'sum', 'sum_distinct',
  'average', 'average_distinct', 'min', 'max', 'median',
  'percentile', 'list', 'number', 'percent_of_total',
  'percent_of_previous', 'running_total',
  'many_to_one', 'many_to_many', 'one_to_one', 'one_to_many',
  'left_outer', 'inner', 'full_outer', 'cross',
  'yes', 'no',
])

const lookmlDef = {
  startState() {
    return { inString: false }
  },

  token(stream, state) {
    // 문자열 내부 처리
    if (state.inString) {
      while (!stream.eol()) {
        const ch = stream.next()
        if (ch === '"') {
          state.inString = false
          return 'string'
        }
        if (ch === '\\') stream.next()  // 이스케이프
      }
      return 'string'
    }

    // 공백 건너뛰기
    if (stream.eatSpace()) return null

    // 주석: #
    if (stream.match('#')) {
      stream.skipToEnd()
      return 'comment'
    }

    // 문 종료: ;;
    if (stream.match(';;')) {
      return 'punctuation'
    }

    // 필드 참조: ${...}
    if (stream.match('${')) {
      while (!stream.eol()) {
        if (stream.next() === '}') break
      }
      return 'variableName.special'
    }

    // 문자열: "..."
    if (stream.peek() === '"') {
      stream.next()
      state.inString = true
      while (!stream.eol()) {
        const ch = stream.next()
        if (ch === '"') {
          state.inString = false
          return 'string'
        }
        if (ch === '\\') stream.next()
      }
      return 'string'
    }

    // 숫자
    if (stream.match(/^-?\d+(\.\d+)?/)) {
      return 'number'
    }

    // 중괄호
    if (stream.match('{') || stream.match('}')) {
      return 'brace'
    }

    // 대괄호
    if (stream.match('[') || stream.match(']')) {
      return 'squareBracket'
    }

    // 콜론
    if (stream.match(':')) {
      return 'punctuation'
    }

    // 단어 (키워드/타입 판별)
    if (stream.match(/^[a-zA-Z_]\w*/)) {
      const word = stream.current()
      if (KEYWORDS.has(word)) return 'keyword'
      if (TYPE_VALUES.has(word)) return 'typeName'
      return 'variableName'
    }

    // 백틱 감싼 식별자: `...`
    if (stream.peek() === '`') {
      stream.next()
      while (!stream.eol()) {
        if (stream.next() === '`') break
      }
      return 'string.special'
    }

    // 기타 문자 소비
    stream.next()
    return null
  },
}

export const lookml = () => StreamLanguage.define(lookmlDef)
